import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { UserLicenseType, UserRoles } from '@bb/users';

import { Modal } from 'components';
import { UserLocalOnboardingProfile } from '../../../types';

import { useOrgOnboardingContext } from '../../../contexts/OrgOnboardingContext';

import { useUserOnboardingContext } from '../../../contexts/UserOnboardingContext';

import { CompleteProfileView as BaseCompleteProfileView } from '../../common/CompleteProfileView';
import { OnboardingCompleteProfileFormValues } from '../../../components';

export type CompleteProfileViewProps = RouteComponentProps<{
  hash?: string;
}> & {
  isLicensed: boolean;
  handleGoNextStep: () => void;
  onRequestStepBack?: () => void;
};

const CompleteProfileView = (props: CompleteProfileViewProps) => {
  const { isLicensed, handleGoNextStep, onRequestStepBack } = props;
  const [profileExists, setProfileExists] = useState<boolean>(false);

  const { orgProfile } = useOrgOnboardingContext();
  const { saveLocalHash, userProfile, updateLocalUserProfile } =
    useUserOnboardingContext();

  const EMPTY_UNLICENSED_PROFILE: Omit<UserLocalOnboardingProfile, 'org'> = {
    id: '',
    firstName: '',
    lastName: '',
    avatarFilestackId: '',
    avatarFullFilestackId: '',
    email: '',
    mls_numbers: [],
    address: `${orgProfile?.firstActiveAddress.addressIndex || 0}`,
    location: `${orgProfile?.firstActiveAddress.locationIndex || 0}`,
    // The default role for unlicensed submitters
    role: UserRoles['Global Admin'],
    licenseType: UserLicenseType.UNLICENSED,
    phone: '',
  };

  const newUserClaimHash = uuidv4();

  if (userProfile?.id) {
    saveLocalHash(newUserClaimHash);
  }

  const initialValues: OnboardingCompleteProfileFormValues = isLicensed
    ? userProfile
    : {
        ...EMPTY_UNLICENSED_PROFILE,
        ...userProfile,
        org: orgProfile,
      };

  const handleSubmit = (values: UserLocalOnboardingProfile) => {
    const profileData = { ...values };

    if (isLicensed) {
      updateLocalUserProfile(profileData);
    } else {
      // create new hash, and store unlicensed user profile to local storage
      // (for access in SetPasswordView and ReviewTOSView for epic 3)
      try {
        // save in local storage with the same user context method
        updateLocalUserProfile(profileData, newUserClaimHash);
      } catch (e) {
        Modal.error({
          title: 'Error',
          content:
            'Could not save profile data. Ensure local storage is enabled on your browser and try again.',
        });
      }
    }

    handleGoNextStep();
  };

  const onEmailError = () => {
    setProfileExists(true);
  };

  const additionalProps: {
    onEmailError?: () => void;
    profileExists?: boolean;
  } = {};
  if (!isLicensed) {
    additionalProps.onEmailError = onEmailError;
    additionalProps.profileExists = profileExists;
  }

  return (
    <BaseCompleteProfileView
      isLicensed={isLicensed}
      handleSubmit={handleSubmit}
      initialValues={initialValues}
      onRequestStepBack={onRequestStepBack}
      {...additionalProps}
    />
  );
};

export default CompleteProfileView;
